// =====================================================
// DASHBOARD DE PRECIOS DE MERCADO - CON ECONOMÍAS REGIONALES
// src/pages/Agro/PreciosMercado/index.js
// =====================================================
// Incluye: Rosario, Futuros, Bahía Blanca, FOB, Hacienda, REGIONALES
// =====================================================

import React, { useState, useEffect } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Spinner,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Button,
  Alert,
} from "reactstrap"
import classnames from "classnames"
import DataTable from "components/Tables/DataTable"
import dayjs from "dayjs"
import Tooltip from "components/Tooltip/Tooltip"
import {
  formatearMonto,
  formatearNumero,
  formatearFecha,
} from "helpers/formatters"

import { getPreciosMercado } from "api/agro"
import toastHandler from "toastHandler"

// BDSJEA-536: formato de importes con helpers/formatters (formatearMonto).
// Pesos sin decimales y dolares con 2 decimales, como antes.
const formatPrecioArs = valor => formatearMonto(valor, "$", 0)
const formatPrecioUsd = valor => formatearMonto(valor, "U$S", 2)

const ERRORES_VACIOS = {
  granos: null,
  futuros: null,
  bahia: null,
  fob: null,
  hacienda: null,
  regionales: null,
}

// Colores por categoría regional
const coloresPorCategoria = {
  ALGODON: "info",
  AZUCAR: "warning",
  YERBA_MATE: "success",
  TE: "primary",
  TABACO: "secondary",
  CITRICOS: "warning",
  VID_UVA: "danger",
  FRUTAS_PEPITA: "success",
  ARROZ: "info",
  MANI: "dark",
}

/**
 * BDSJEA-136 - Panel de Precios de Mercado.
 *
 * Antes esto era una pagina con su propia ruta (/agro/precios) y su propio
 * item de menu bajo Agro. Ahora se monta como solapa dentro de
 * Administracion (/agro/admin), asi que el componente devuelve solo su
 * contenido y ya no envuelve nada en <Layout>: el Layout, el titulo y el
 * control de permisos los pone la pagina que lo contiene.
 */
export const PreciosMercadoPanel = () => {
  const { setError } = toastHandler()
  const [loading, setLoading] = useState(true)
  const [precios, setPrecios] = useState({
    granos: [],
    futuros: [],
    preciosBahiaBlanca: [],
    preciosFob: [],
    hacienda: [],
    regionales: null, // Nuevo: precios regionales
    fuentesDisponibles: [],
  })
  // BDSJEA-536: mensajes de error por fuente que informa el backend
  const [errores, setErrores] = useState(ERRORES_VACIOS)
  const [activeTab, setActiveTab] = useState("rosario")
  const [fechaConsulta, setFechaConsulta] = useState(null)

  useEffect(() => {
    loadPrecios()
  }, [])

  const loadPrecios = () => {
    setLoading(true)
    setErrores(ERRORES_VACIOS)
    getPreciosMercado()
      .then(data => {
        setPrecios({
          granos: data.granos || [],
          futuros: data.futuros || [],
          preciosBahiaBlanca: data.preciosBahiaBlanca || [],
          preciosFob: data.preciosFob || [],
          hacienda: data.hacienda || [],
          regionales: data.regionales || null, // Nuevo
          fuentesDisponibles: data.fuentesDisponibles || [],
        })
        setErrores({
          granos: data.errorGranos || null,
          futuros: data.errorFuturos || null,
          bahia: data.errorBahiaBlanca || null,
          fob: data.errorFob || null,
          hacienda: data.errorHacienda || null,
          regionales: data.errorRegionales || null,
        })
        setFechaConsulta(data.fechaConsulta)
      })
      .catch(err => {
        setError(err)
        setErrores(ERRORES_VACIOS)
        setPrecios({
          granos: [],
          futuros: [],
          preciosBahiaBlanca: [],
          preciosFob: [],
          hacienda: [],
          regionales: null,
          fuentesDisponibles: [],
        })
      })
      .finally(() => setLoading(false))
  }

  // Contar total de precios regionales
  const contarRegionales = () => {
    if (!precios.regionales) return 0
    const r = precios.regionales
    let total = 0
    if (r.preciosAlgodon) total += r.preciosAlgodon.length
    if (r.preciosAzucar) total += r.preciosAzucar.length
    if (r.preciosYerbaMate) total += r.preciosYerbaMate.length
    if (r.preciosTe) total += r.preciosTe.length
    if (r.preciosTabaco) total += r.preciosTabaco.length
    if (r.preciosCitricos) total += r.preciosCitricos.length
    if (r.preciosVid) total += r.preciosVid.length
    if (r.preciosFrutasPepita) total += r.preciosFrutasPepita.length
    if (r.preciosArroz) total += r.preciosArroz.length
    if (r.preciosMani) total += r.preciosMani.length
    return total
  }

  // === COLUMNAS ROSARIO (ARS) ===
  const columnasRosario = [
    {
      name: "Cultivo",
      selector: row => row.cultivo,
      sortable: true,
      cell: row => (
        <div>
          <strong>{row.cultivo}</strong>
          <br />
          <small className="text-muted">{row.codigoCultivo}</small>
        </div>
      ),
    },
    {
      name: "Precio $/Tn",
      selector: row => row.precioArsTn,
      sortable: true,
      right: true,
      cell: row => (
        <strong className="text-success">
          {formatPrecioArs(row.precioArsTn)}
        </strong>
      ),
    },
    {
      name: "Variación",
      selector: row => row.variacionDiariaPct,
      sortable: true,
      right: true,
      cell: row => {
        if (row.variacionDiariaPct == null) return "-"
        const color = row.variacionDiariaPct >= 0 ? "success" : "danger"
        const signo = row.variacionDiariaPct >= 0 ? "+" : ""
        return (
          <Badge color={color}>
            {signo}
            {formatearNumero(row.variacionDiariaPct, 2)}%
          </Badge>
        )
      },
    },
  ]

  // === COLUMNAS FUTUROS (USD) ===
  const columnasFuturos = [
    {
      name: "Cultivo",
      selector: row => row.cultivo,
      sortable: true,
      width: "120px",
    },
    {
      name: "Posición",
      selector: row => row.posicion,
      sortable: true,
      width: "100px",
      cell: row => <Badge color="primary">{row.posicion}</Badge>,
    },
    {
      name: "Precio USD/Tn",
      selector: row => row.precioUsdTn,
      sortable: true,
      right: true,
      cell: row => (
        <strong className="text-info">
          {formatPrecioUsd(row.precioUsdTn)}
        </strong>
      ),
    },
    {
      name: "Variación",
      selector: row => row.variacionDiariaPct,
      sortable: true,
      right: true,
      cell: row => {
        if (row.variacionDiariaPct == null) return "-"
        const color = row.variacionDiariaPct >= 0 ? "success" : "danger"
        const signo = row.variacionDiariaPct >= 0 ? "+" : ""
        return (
          <Badge color={color}>
            {signo}
            {formatearNumero(row.variacionDiariaPct, 2)}%
          </Badge>
        )
      },
    },
  ]

  // === COLUMNAS BAHÍA BLANCA ===
  // BDSJEA-536: precio en USD y/o en pesos segun lo que publique la Camara
  // (sin dato = "-"), fecha de la cotizacion y marca de precio estimativo.
  const columnasBahia = [
    {
      name: "Cultivo",
      selector: row => row.cultivo,
      sortable: true,
      cell: row => (
        <div>
          {row.cultivo}
          {String(row.condicion || "").toUpperCase() === "ESTIMATIVO" && (
            <Badge color="warning" className="ms-2">
              Estimativo
            </Badge>
          )}
        </div>
      ),
    },
    {
      name: "Precio USD/Tn",
      selector: row => row.precioUsdTn,
      sortable: true,
      right: true,
      cell: row => (
        <strong className="text-info">
          {formatPrecioUsd(row.precioUsdTn)}
        </strong>
      ),
    },
    {
      name: "Precio $/Tn",
      selector: row => row.precioArsTn,
      sortable: true,
      right: true,
      cell: row => (
        <strong className="text-info">
          {formatPrecioArs(row.precioArsTn)}
        </strong>
      ),
    },
    {
      name: "Fecha",
      selector: row => row.fecha,
      sortable: true,
      right: true,
      cell: row => formatearFecha(row.fecha),
    },
  ]

  // === COLUMNAS FOB (USD) ===
  const columnasFob = [
    {
      name: "Cultivo",
      selector: row => row.cultivo,
      sortable: true,
    },
    {
      name: "FOB USD/Tn",
      selector: row => row.precioUsdTn,
      sortable: true,
      right: true,
      cell: row => (
        <strong className="text-primary">
          {formatPrecioUsd(row.precioUsdTn)}
        </strong>
      ),
    },
    {
      name: "FAS $/Tn",
      selector: row => row.precioArsTn,
      sortable: true,
      right: true,
      cell: row => formatPrecioArs(row.precioArsTn),
    },
  ]

  // === COLUMNAS HACIENDA ===
  const columnasHacienda = [
    {
      name: "Categoría",
      selector: row => row.categoria,
      sortable: true,
      grow: 2,
      cell: row => (
        <div>
          <strong>{row.categoria}</strong>
          <br />
          <Badge color="secondary" size="sm">
            {row.codigoCategoria}
          </Badge>
        </div>
      ),
    },
    {
      name: "Mínimo",
      selector: row => row.precioMinKg,
      right: true,
      cell: row => formatPrecioArs(row.precioMinKg),
    },
    {
      name: "Máximo",
      selector: row => row.precioMaxKg,
      right: true,
      cell: row => formatPrecioArs(row.precioMaxKg),
    },
    {
      name: "Promedio",
      selector: row => row.precioPromedioKg,
      right: true,
      cell: row => <strong>{formatPrecioArs(row.precioPromedioKg)}</strong>,
    },
  ]

  // === COLUMNAS REGIONALES ===
  const columnasRegionales = [
    {
      name: "Cultivo",
      selector: row => row.cultivo,
      sortable: true,
      grow: 2,
      cell: row => (
        <div>
          <strong>{row.cultivo}</strong>
          <br />
          <Badge
            color={coloresPorCategoria[row.categoria?.nombre] || "secondary"}
            size="sm"
          >
            {row.region || "Regional"}
          </Badge>
        </div>
      ),
    },
    {
      name: "Precio",
      selector: row => row.precioArsPorUnidad,
      sortable: true,
      right: true,
      cell: row => (
        <div className="text-end">
          <strong className="text-success">
            {row.precioArsPorUnidad
              ? formatPrecioArs(row.precioArsPorUnidad)
              : formatPrecioUsd(row.precioUsdPorUnidad)}
          </strong>
          <br />
          <small className="text-muted">/{row.unidad || "tn"}</small>
        </div>
      ),
    },
    {
      name: "Mercado",
      selector: row => row.mercado,
      cell: row => <small className="text-muted">{row.mercado}</small>,
    },
  ]

  // === COMPONENTE PARA MOSTRAR REGIONALES POR CATEGORÍA ===
  const RegionalesContent = () => {
    const r = precios.regionales
    if (!r) {
      return (
        <div className="text-center py-4">
          <i
            className="mdi mdi-map-marker-off text-muted"
            style={{ fontSize: "3rem" }}
          />
          <p className="text-muted mt-2">
            {errores.regionales || "No hay precios regionales disponibles"}
          </p>
        </div>
      )
    }

    // Agrupar todos los precios
    const todosLosPrecios = [
      ...(r.preciosAlgodon || []),
      ...(r.preciosAzucar || []),
      ...(r.preciosYerbaMate || []),
      ...(r.preciosTe || []),
      ...(r.preciosTabaco || []),
      ...(r.preciosCitricos || []),
      ...(r.preciosVid || []),
      ...(r.preciosFrutasPepita || []),
      ...(r.preciosArroz || []),
      ...(r.preciosMani || []),
      ...(r.preciosOtros || []),
    ]

    if (todosLosPrecios.length === 0) {
      return (
        <div className="text-center py-4">
          <i
            className="mdi mdi-map-marker-off text-muted"
            style={{ fontSize: "3rem" }}
          />
          <p className="text-muted mt-2">
            {errores.regionales || "No hay precios regionales disponibles"}
          </p>
        </div>
      )
    }

    return (
      <>
        {errores.regionales && (
          <Alert color="warning" className="py-1 mb-2">
            <small>{errores.regionales}</small>
          </Alert>
        )}
        {/* Resumen por región */}
        <Row className="mb-3">
          <Col>
            <div className="d-flex flex-wrap gap-2">
              {r.preciosAlgodon?.length > 0 && (
                <Badge color="info" className="p-2">
                  <i className="mdi mdi-leaf me-1" />
                  Algodón ({r.preciosAlgodon.length})
                </Badge>
              )}
              {r.preciosAzucar?.length > 0 && (
                <Badge color="warning" className="p-2">
                  <i className="mdi mdi-cube-outline me-1" />
                  Azúcar ({r.preciosAzucar.length})
                </Badge>
              )}
              {r.preciosYerbaMate?.length > 0 && (
                <Badge color="success" className="p-2">
                  <i className="mdi mdi-tea me-1" />
                  Yerba Mate ({r.preciosYerbaMate.length})
                </Badge>
              )}
              {r.preciosCitricos?.length > 0 && (
                <Badge color="warning" className="p-2">
                  <i className="mdi mdi-fruit-citrus me-1" />
                  Cítricos ({r.preciosCitricos.length})
                </Badge>
              )}
              {r.preciosVid?.length > 0 && (
                <Badge color="danger" className="p-2">
                  <i className="mdi mdi-fruit-grapes me-1" />
                  Vid/Uva ({r.preciosVid.length})
                </Badge>
              )}
              {r.preciosFrutasPepita?.length > 0 && (
                <Badge color="success" className="p-2">
                  <i className="mdi mdi-apple me-1" />
                  Frutas Pepita ({r.preciosFrutasPepita.length})
                </Badge>
              )}
            </div>
          </Col>
        </Row>

        {/* Tabla de precios */}
        <DataTable
          columns={columnasRegionales}
          data={todosLosPrecios}
          pagination
          paginationPerPage={10}
          highlightOnHover
          striped
          responsive
        />

        {/* Fuentes */}
        <div className="mt-3 text-muted">
          <small>
            <i className="mdi mdi-information-outline me-1" />
            Fuentes:{" "}
            {r.fuentesDisponibles?.join(", ") || "Economías Regionales"}
          </small>
        </div>

        {/* Errores si los hay */}
        {r.errorAlgodon && (
          <Alert color="warning" className="mt-2 mb-0 py-1">
            <small>Algodón: {r.errorAlgodon}</small>
          </Alert>
        )}
        {r.errorAzucar && (
          <Alert color="warning" className="mt-2 mb-0 py-1">
            <small>Azúcar: {r.errorAzucar}</small>
          </Alert>
        )}
        {r.errorYerbaMate && (
          <Alert color="warning" className="mt-2 mb-0 py-1">
            <small>Yerba Mate: {r.errorYerbaMate}</small>
          </Alert>
        )}
      </>
    )
  }

  // === CONTENIDO PRINCIPAL ===
  const MainContent = () => (
    <Row>
      <Col xs={12}>
        <Card>
          <CardBody>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 className="mb-0">
                  <i className="mdi mdi-chart-line me-2" />
                  Precios de Mercado
                </h5>
                {fechaConsulta && (
                  <small className="text-muted">
                    Fecha: {dayjs(fechaConsulta).format("DD/MM/YYYY")}
                  </small>
                )}
              </div>
              <div>
                {precios.fuentesDisponibles.length > 0 && (
                  <span className="me-3">
                    <Tooltip title={precios.fuentesDisponibles.join(", ")}>
                      <span>
                        <Badge color="light" className="text-dark">
                          {precios.fuentesDisponibles.length} fuentes
                        </Badge>
                      </span>
                    </Tooltip>
                  </span>
                )}
                <Button
                  color="primary"
                  size="sm"
                  onClick={loadPrecios}
                  disabled={loading}
                >
                  <i className="bx bx-refresh me-1" />
                  Actualizar
                </Button>
              </div>
            </div>

            {/* Tabs de navegación */}
            <style>
              {`
                .nav-tabs-precios .nav-link.active {
                  color: #495057 !important;
                  background-color: #fff !important;
                  font-weight: 600 !important;
                  border-color: #dee2e6 #dee2e6 #fff !important;
                }
                .nav-tabs-precios .nav-link {
                  color: #6c757d !important;
                  cursor: pointer;
                  padding: 0.5rem 1rem;
                }
                .nav-tabs-precios .nav-link:hover:not(.active) {
                  color: #495057 !important;
                  border-color: #e9ecef #e9ecef #dee2e6 !important;
                }
                .nav-tabs-precios .nav-link.tab-regionales {
                  background-color: #e8f5e9 !important;
                  border-color: #c8e6c9 !important;
                }
                .nav-tabs-precios .nav-link.tab-regionales.active {
                  background-color: #fff !important;
                  border-bottom-color: #fff !important;
                }
              `}
            </style>
            <Nav tabs className="mb-3 nav-tabs-precios flex-wrap">
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "rosario" })}
                  onClick={() => setActiveTab("rosario")}
                >
                  <i className="mdi mdi-map-marker me-1" />
                  Rosario ({precios.granos.length})
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "futuros" })}
                  onClick={() => setActiveTab("futuros")}
                >
                  <i className="mdi mdi-chart-timeline-variant me-1" />
                  Futuros ({precios.futuros.length})
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "bahia" })}
                  onClick={() => setActiveTab("bahia")}
                >
                  <i className="mdi mdi-anchor me-1" />
                  Bahía Blanca ({precios.preciosBahiaBlanca.length})
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "fob" })}
                  onClick={() => setActiveTab("fob")}
                >
                  <i className="mdi mdi-ferry me-1" />
                  FOB Oficial ({precios.preciosFob.length})
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "hacienda" })}
                  onClick={() => setActiveTab("hacienda")}
                >
                  <i className="mdi mdi-cow me-1" />
                  Hacienda ({precios.hacienda.length})
                </NavLink>
              </NavItem>
              {/* NUEVO TAB: REGIONALES */}
              <NavItem>
                <NavLink
                  className={classnames("tab-regionales", {
                    active: activeTab === "regionales",
                  })}
                  onClick={() => setActiveTab("regionales")}
                >
                  <i className="mdi mdi-map-marker-multiple me-1" />
                  Regionales ({contarRegionales()})
                </NavLink>
              </NavItem>
            </Nav>

            {/* Contenido de tabs */}
            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2 text-muted">
                  Cargando precios de mercado...
                </p>
              </div>
            ) : (
              <TabContent activeTab={activeTab}>
                {/* ROSARIO */}
                <TabPane tabId="rosario">
                  <TablaPrecios
                    data={precios.granos}
                    error={errores.granos}
                    columns={columnasRosario}
                    fuente="CAC-BCR (Cámara Arbitral de Cereales - Rosario)"
                    emptyIcon="mdi-seed-off"
                    emptyText="No hay precios de Rosario disponibles"
                  />
                </TabPane>

                {/* FUTUROS */}
                <TabPane tabId="futuros">
                  <TablaPrecios
                    data={precios.futuros}
                    error={errores.futuros}
                    columns={columnasFuturos}
                    fuente="MATBA-Rofex (Mercado a Término de Buenos Aires)"
                    emptyIcon="mdi-chart-timeline-variant"
                    emptyText="No hay precios futuros disponibles"
                  />
                </TabPane>

                {/* BAHÍA BLANCA */}
                <TabPane tabId="bahia">
                  <TablaPrecios
                    data={precios.preciosBahiaBlanca}
                    error={errores.bahia}
                    columns={columnasBahia}
                    fuente="CACBB (Cámara Arbitral de Cereales - Bahía Blanca)"
                    emptyIcon="mdi-anchor"
                    emptyText="No hay precios de Bahía Blanca disponibles"
                  />
                </TabPane>

                {/* FOB OFICIAL */}
                <TabPane tabId="fob">
                  <TablaPrecios
                    data={precios.preciosFob}
                    error={errores.fob}
                    columns={columnasFob}
                    fuente="MAGYP (Ministerio de Agricultura - FOB Ley 21.453)"
                    emptyIcon="mdi-ferry"
                    emptyText="No hay precios FOB oficiales disponibles"
                  />
                </TabPane>

                {/* HACIENDA */}
                <TabPane tabId="hacienda">
                  <TablaPrecios
                    data={precios.hacienda}
                    error={errores.hacienda}
                    columns={columnasHacienda}
                    fuente="Mercado Agroganadero (ex-Liniers)"
                    emptyIcon="mdi-cow-off"
                    emptyText="No hay precios de hacienda disponibles"
                  />
                </TabPane>

                {/* REGIONALES - NUEVO */}
                <TabPane tabId="regionales">
                  <RegionalesContent />
                </TabPane>
              </TabContent>
            )}
          </CardBody>
        </Card>
      </Col>
    </Row>
  )

  // Componente reutilizable para cada tabla
  const TablaPrecios = ({
    data,
    columns,
    fuente,
    emptyIcon,
    emptyText,
    error,
  }) => (
    <>
      {data.length === 0 ? (
        <div className="text-center py-4">
          <i
            className={`mdi ${emptyIcon} text-muted`}
            style={{ fontSize: "3rem" }}
          />
          <p className="text-muted mt-2">{error || emptyText}</p>
        </div>
      ) : (
        <>
          {error && (
            <Alert color="warning" className="py-1 mb-2">
              <small>{error}</small>
            </Alert>
          )}
          <DataTable
            columns={columns}
            data={data}
            pagination
            paginationPerPage={10}
            highlightOnHover
            striped
            responsive
          />
          <div className="mt-3 text-muted">
            <small>
              <i className="mdi mdi-information-outline me-1" />
              Fuente: {fuente}
            </small>
          </div>
        </>
      )}
    </>
  )

  return <MainContent />
}

export default PreciosMercadoPanel
